import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Brain, ChevronDown, ChevronUp, RefreshCw, DollarSign, Timer, FlaskConical, Play, Wallet, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import AssuranceCard from '@/components/AssuranceCard';
import EvalsCard from '@/components/EvalsCard';
import ScorecardCard from '@/components/ScorecardCard';
import InvariantsCard from '@/components/InvariantsCard';

const C = { navy: '#0B1E3D', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', amber: '#E8A020', red: '#E24B4A' };
const OUTCOME = { ok: C.green, fallback: C.amber, error: C.red };

const fmt$ = (v) => `$${(v || 0).toFixed(2)}`;
const fmtLat = (ms) => (ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

function EvalHarnessCard({ api }) {
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [showCases, setShowCases] = useState(false);
  const [goldenCount, setGoldenCount] = useState(null);

  useEffect(() => {
    api.get('/platform/evals/goldens').then(r => setGoldenCount(r.data.active)).catch(() => {});
  }, [api]);

  const loadLatest = useCallback(() => api.get('/platform/evals?limit=1')
    .then(r => setRun(r.data.runs?.[0] || null)).catch(() => {}), [api]);
  useEffect(() => { loadLatest(); }, [loadLatest]);

  useEffect(() => {
    if (run?.status !== 'running') { setRunning(false); return undefined; }
    setRunning(true);
    const t = setInterval(loadLatest, 3000);
    return () => clearInterval(t);
  }, [run?.status, loadLatest]);

  const start = async () => {
    setRunning(true);
    try { await api.post('/platform/evals/run'); loadLatest(); }
    catch { setRunning(false); }
  };

  const totals = run?.totals;
  const allPass = totals && totals.failed === 0;
  const suites = {};
  (run?.cases || []).forEach(c => {
    const s = suites[c.suite] = suites[c.suite] || { passed: 0, failed: 0 };
    c.passed ? (s.passed += 1) : (s.failed += 1);
  });
  const failedCases = (run?.cases || []).filter(c => !c.passed);

  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.18)' }} data-testid="eval-harness-card">
      <div className="flex flex-wrap items-center gap-3">
        <FlaskConical className="w-4 h-4" style={{ color: C.blue }} />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Eval Harness — Golden Regression Suite</span>
        {goldenCount != null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-semibold" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }} data-testid="eval-pinned-count">
            {goldenCount} pinned golden{goldenCount === 1 ? '' : 's'}
          </span>
        )}
        {run?.status === 'running' || running ? (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: C.amber }} data-testid="eval-running">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.amber }} />Running…
          </span>
        ) : totals ? (
          <>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-sm uppercase" data-testid="eval-status"
              style={{ background: allPass ? 'rgba(29,158,117,0.12)' : 'rgba(226,75,74,0.12)', color: allPass ? C.green : C.red }}>
              {allPass ? `PASS ${totals.passed}/${totals.cases}` : `FAIL ${totals.failed}/${totals.cases}`}
            </span>
            {Object.entries(suites).map(([s, v]) => (
              <span key={s} className="text-[10px] font-mono" style={{ color: v.failed ? C.red : C.slate }}>{s}: {v.passed}/{v.passed + v.failed}</span>
            ))}
            <span className="text-[10px]" style={{ color: C.slate }}>
              {new Date(run.started_at).toLocaleString('en-AU')} · by {run.triggered_by} · prompts {run.context?.prompt_versions?.risk_narrative}
            </span>
          </>
        ) : (
          <span className="text-xs" style={{ color: C.slate }}>No runs yet — run before shipping any prompt, model or scoring change</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          {run?.cases?.length > 0 && (
            <button onClick={() => setShowCases(!showCases)} data-testid="eval-toggle-cases"
              className="text-[11px] font-semibold px-2 py-1.5 rounded-sm border transition-colors hover:bg-[#F5F7FA]"
              style={{ color: C.blue, borderColor: 'rgba(26,76,124,0.3)' }}>{showCases ? 'Hide cases' : `${run.cases.length} cases`}</button>
          )}
          <button onClick={start} disabled={running || run?.status === 'running'} data-testid="eval-run-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: C.blue }}>
            <Play className="w-3 h-3" />Run evals
          </button>
        </div>
      </div>
      {failedCases.length > 0 && (
        <div className="mt-3 space-y-1">
          {failedCases.map(c => (
            <p key={c.case_id} className="text-[11px]" style={{ color: C.red }} data-testid={`eval-fail-${c.case_id}`}>
              ✗ {c.suite}/{c.case_id} {c.name} — {(c.details || []).join('; ')}
            </p>
          ))}
        </div>
      )}
      {showCases && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {(run?.cases || []).map(c => (
            <div key={c.case_id} className="flex items-start gap-2 text-[11px]">
              <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: c.passed ? C.green : C.red }} />
              <span className="font-mono font-semibold" style={{ color: C.navy }}>{c.case_id}</span>
              <span style={{ color: C.slate }}>{c.name}{c.duration_ms > 500 ? ` (${(c.duration_ms / 1000).toFixed(1)}s)` : ''}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

const LEVEL = {
  breach: { color: C.red, label: 'Budget exceeded' },
  warning: { color: C.amber, label: 'Approaching budget' },
  ok: { color: C.green, label: 'Within budget' },
  off: { color: C.slate, label: 'No budget set' },
};

function BudgetCard({ api }) {
  const [status, setStatus] = useState(null);
  const [val, setVal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/platform/ai-budget').then(r => {
      setStatus(r.data);
      setVal(r.data.config.monthly_budget_usd ? String(r.data.config.monthly_budget_usd) : '');
    }).catch(() => {});
  }, [api]);

  const save = async () => {
    const num = parseFloat(val);
    if (Number.isNaN(num) || num < 0) { toast.error('Enter a valid budget amount'); return; }
    setSaving(true);
    try {
      const r = await api.put('/platform/ai-budget', { monthly_budget_usd: num });
      setStatus(r.data);
      toast.success(num ? `Monthly LLM budget set to $${num.toFixed(2)} — ARIA is watching`
        : 'Budget cleared — ARIA budget alerts off');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save budget');
    } finally { setSaving(false); }
  };

  if (!status) return null;
  const { spend, pct_of_budget: pct, level, config, last_alert } = status;
  const lv = LEVEL[level] || LEVEL.off;
  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.18)' }} data-testid="ai-budget-card">
      <div className="flex flex-wrap items-center gap-3">
        <Wallet className="w-4 h-4" style={{ color: C.blue }} />
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>ARIA Budget Governor</span>
        <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm" data-testid="ai-budget-level"
          style={{ background: `${lv.color}1A`, color: lv.color }}>{lv.label}</span>
        {level !== 'off' && (
          <span className="text-xs font-mono" style={{ color: C.navy }} data-testid="ai-budget-usage">
            {fmt$(spend.est_cost_usd)} of {fmt$(config.monthly_budget_usd)} · <b style={{ color: lv.color }}>{pct}%</b>
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px]" style={{ color: C.slate }}>Monthly budget (USD)</span>
          <input type="number" min="0" step="1" value={val} onChange={e => setVal(e.target.value)}
            placeholder="e.g. 50" data-testid="ai-budget-input"
            className="w-24 px-2 py-1.5 text-xs font-mono rounded-sm border focus:outline-none"
            style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }} />
          <button onClick={save} disabled={saving} data-testid="ai-budget-save"
            className="px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
            style={{ background: C.navy }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      {level !== 'off' ? (
        <>
          <div className="mt-3 h-2 rounded-sm overflow-hidden" style={{ background: 'rgba(136,150,168,0.15)' }}>
            <div className="h-full transition-all" data-testid="ai-budget-bar"
              style={{ width: `${Math.min(pct, 100)}%`, background: lv.color }} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px]" style={{ color: C.slate }}>
            <span>{spend.month} · {spend.calls.toLocaleString()} LLM calls</span>
            <span>Warn at {config.warn_pct}% · alert at 100%</span>
            {last_alert?.alerted_level && last_alert?.month === spend.month && (
              <span className="flex items-center gap-1" data-testid="ai-budget-last-alert" style={{ color: LEVEL[last_alert.alerted_level]?.color }}>
                <Sparkles className="w-3 h-3" />
                ARIA raised a {last_alert.alerted_level} on {new Date(last_alert.alerted_at).toLocaleString('en-AU')}
              </span>
            )}
          </div>
        </>
      ) : (
        <p className="mt-2 text-[11px]" style={{ color: C.slate }}>
          Set a monthly budget and ARIA warns automatically at {config.warn_pct}% and escalates when spend crosses 100% — checked every 30 minutes.
        </p>
      )}
    </Card>
  );
}

function CostMeter({ stats }) {
  const thisMonth = stats.months?.[0];
  return (
    <Card className="p-4 rounded-sm" style={{ border: '1px solid rgba(136,150,168,0.18)' }} data-testid="ai-cost-meter">
      <div className="flex flex-wrap items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4" style={{ color: C.green }} />
          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>LLM Spend & Latency</span>
        </div>
        {thisMonth && (
          <span className="text-xs" style={{ color: C.slate }} data-testid="ai-cost-month">
            {thisMonth.month}: <strong className="font-mono" style={{ color: C.navy }}>{fmt$(thisMonth.est_cost_usd)}</strong> est · {thisMonth.calls.toLocaleString()} calls
            {Object.entries(thisMonth.by_model || {}).map(([m, v]) => (
              <span key={m} className="ml-2 text-[10px] px-1.5 py-0.5 rounded-sm font-mono" style={{ background: 'rgba(26,76,124,0.06)', color: C.blue }}>
                {m.split('/').pop()}: {fmt$(v.est_cost_usd)}
              </span>
            ))}
          </span>
        )}
        <span className="ml-auto text-[10px]" style={{ color: C.slate }}>{stats.note}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b" style={{ borderColor: 'rgba(136,150,168,0.2)' }}>
              {['Call site', 'Calls', 'Avg latency', 'Est. cost', 'By model'].map(h => (
                <th key={h} className="py-1.5 pr-4 text-[9px] uppercase tracking-wider font-bold" style={{ color: C.slate }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(stats.call_sites || []).map(s => (
              <tr key={s.call_site} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`ai-cost-site-${s.call_site}`}>
                <td className="py-1.5 pr-4 text-xs font-mono font-semibold" style={{ color: C.navy }}>{s.call_site}</td>
                <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.slate }}>{s.calls.toLocaleString()}</td>
                <td className="py-1.5 pr-4 text-xs font-mono flex items-center gap-1" style={{ color: (s.avg_latency_ms || 0) > 10000 ? C.amber : C.green }}>
                  <Timer className="w-3 h-3" />{fmtLat(s.avg_latency_ms)}
                </td>
                <td className="py-1.5 pr-4 text-xs font-mono" style={{ color: C.navy }}>{fmt$(s.est_cost_usd)}</td>
                <td className="py-1.5 text-[10px]" style={{ color: C.slate }}>
                  {Object.entries(s.models || {}).map(([m, v]) => (
                    <span key={m} className="mr-2 whitespace-nowrap">{m.split('/').pop()} ({v.calls} · {fmtLat(v.avg_latency_ms)})</span>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function AiAuditPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [site, setSite] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/platform/ai-audit?call_site=${site}&limit=100`),
      api.get('/platform/ai-audit/stats').catch(() => ({ data: null })),
    ]).then(([r, s]) => { setData(r.data); setStats(s.data); })
      .catch(e => console.error(e?.message)).finally(() => setLoading(false));
  }, [api, site]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</div>;

  return (
    <div className="space-y-4" data-testid="ai-audit-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: 'Georgia, serif' }}>AI Call Audit</h1>
          <p className="text-sm mt-1" style={{ color: C.slate }}>
            Every LLM call recorded with prompt version, model version and raw output — {data?.total?.toLocaleString() || 0} entries
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select value={site} onChange={e => setSite(e.target.value)} data-testid="ai-audit-site-filter"
            className="px-2.5 py-1.5 text-xs rounded-sm border focus:outline-none" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}>
            <option value="">All call sites</option>
            {(data?.call_sites || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={load} data-testid="ai-audit-refresh" className="p-2 rounded-sm border transition-colors hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.3)' }}>
            <RefreshCw className="w-3.5 h-3.5" style={{ color: C.blue }} />
          </button>
        </div>
      </div>

      <BudgetCard api={api} />

      <AssuranceCard api={api} />

      <EvalsCard api={api} />

      <ScorecardCard api={api} />

      <InvariantsCard api={api} />

      {stats && <CostMeter stats={stats} />}

      <EvalHarnessCard api={api} />

      {(data?.entries || []).length === 0 && (
        <Card className="p-6 rounded-sm text-center" data-testid="ai-audit-empty">
          <Brain className="w-6 h-6 mx-auto mb-2" style={{ color: C.slate }} />
          <p className="text-sm" style={{ color: C.slate }}>No AI calls recorded yet — entries appear here as ARIA works.</p>
        </Card>
      )}

      <div className="space-y-2">
        {(data?.entries || []).map((e, i) => {
          const oc = OUTCOME[e.outcome] || C.slate;
          const isOpen = open === i;
          return (
            <Card key={i} className="rounded-sm overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.18)' }} data-testid={`ai-audit-row-${i}`}>
              <button onClick={() => setOpen(isOpen ? null : i)} className="w-full px-3 py-2.5 flex flex-wrap items-center gap-2 text-left hover:bg-[#F5F7FA]">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: oc }} />
                <span className="text-xs font-mono font-bold" style={{ color: C.navy }}>{e.call_site}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-semibold" style={{ background: 'rgba(26,76,124,0.08)', color: C.blue }}>{e.prompt_version}</span>
                <span className="text-[10px] font-mono" style={{ color: C.slate }}>{e.model}</span>
                <span className="text-[9px] font-bold uppercase" style={{ color: oc }}>{e.outcome}</span>
                {e.latency_ms != null && <span className="text-[10px] font-mono" style={{ color: C.slate }}>{(e.latency_ms / 1000).toFixed(1)}s</span>}
                <span className="ml-auto text-[10px]" style={{ color: C.slate }}>
                  {e.target?.type}{e.target?.id ? ` · ${String(e.target.id).slice(0, 8)}` : ''} · {new Date(e.timestamp).toLocaleString('en-AU')}
                </span>
                {isOpen ? <ChevronUp className="w-3.5 h-3.5" style={{ color: C.slate }} /> : <ChevronDown className="w-3.5 h-3.5" style={{ color: C.slate }} />}
              </button>
              {isOpen && (
                <div className="px-3 pb-3 space-y-2 border-t" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                  <p className="text-[9px] mt-2 font-mono" style={{ color: C.slate }}>prompt sha256: {e.prompt_sha256}</p>
                  {e.error && <p className="text-[11px] font-semibold" style={{ color: C.red }}>Error: {e.error}</p>}
                  {[['System message', e.system_message], ['User prompt', e.user_prompt], ['Raw model output', e.raw_output]].map(([label, text]) => text && (
                    <div key={label}>
                      <p className="text-[9px] uppercase tracking-wider font-bold mb-0.5" style={{ color: C.slate }}>{label}</p>
                      <pre className="text-[10px] p-2 rounded-sm whitespace-pre-wrap max-h-56 overflow-y-auto" style={{ background: '#F5F7FA', color: '#1A2733' }}>{text}</pre>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
